/**
 * Sample Visions - Pre-configured example scenarios
 * These are loaded automatically for new users to help them get started
 */

export const sampleVisions = [
  {
    name: 'Software Engineer — FIRE at 45',
    currentAge: 30,
    retireAge: 45,
    autoCalculateRetireAge: false,  // Fixed - FIRE goal is explicit
    currentSalary: 180000,  // FAANG-level salary (realistic for FIRE trajectory)
    salaryGrowthRate: 5,  // Strong progression with promotions
    currentPortfolio: 150000,  // Already saving aggressively by 30
    monthlyContribution: 7500,  // ~50% savings rate needed for FIRE in 15 years
    expectedReturn: 8,
    inflation: 3,
    retirementIncome: 0,
    retirementExpenses: 5000,  // Modest post-FIRE lifestyle
    safeWithdrawalRate: 3.5,
    socialSecurityAge: 67,
    socialSecurityMonthly: 2800,

    housing: {
      type: 'own',
      monthlyPayment: 2200,  // Reasonable for high income
      startYear: 0,
      endYear: 15,  // 15-year mortgage to pay off by FIRE
      propertyValue: 450000,
      downPayment: 100000,
      mortgageRate: 6.5,
      mortgageTerm: 15,
      propertyTaxRate: 1.2,
      insuranceMonthly: 150,
      maintenanceRate: 1,
      appreciationRate: 3.5,
      rentGrowthRate: 0
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 3200,  // Frugal for income level - key to FIRE
      healthcare: 350,
      transportation: 500,  // One reliable car
      entertainment: 400,
      other: 400
    }
  },

  {
    name: 'Coast FIRE at 35',
    currentAge: 25,
    retireAge: 60,
    autoCalculateRetireAge: true,  // Calculate when they can fully retire
    currentSalary: 95000,
    salaryGrowthRate: 3.5,
    currentPortfolio: 50000,  // Strong start - aggressive saving in 20s
    monthlyContribution: 3500,  // High savings rate to reach coast number by 35
    expectedReturn: 8,
    inflation: 3,
    retirementIncome: 0,
    retirementExpenses: 4200,
    safeWithdrawalRate: 4,
    socialSecurityAge: 67,
    socialSecurityMonthly: 2400,

    housing: {
      type: 'rent',
      monthlyPayment: 1500,
      startYear: 0,
      endYear: 40,
      propertyValue: 0,
      downPayment: 0,
      mortgageRate: 0,
      mortgageTerm: 0,
      propertyTaxRate: 0,
      insuranceMonthly: 0,
      maintenanceRate: 0,
      appreciationRate: 0,
      rentGrowthRate: 3
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 2500,  // Frugal to enable high savings
      healthcare: 300,
      transportation: 350,
      entertainment: 300,
      other: 250
    }
  },

  {
    name: 'DINK Couple (Dual Income)',
    currentAge: 32,
    retireAge: 50,
    autoCalculateRetireAge: false,  // Fixed - FIRE goal at 50 is explicit
    currentSalary: 200000,  // Combined dual income (100k each)
    salaryGrowthRate: 3.5,
    currentPortfolio: 200000,  // Strong portfolio by 32 with dual income
    monthlyContribution: 7000,  // ~42% savings rate to hit FIRE at 50
    expectedReturn: 8,
    inflation: 3,
    retirementIncome: 0,
    retirementExpenses: 6500,
    safeWithdrawalRate: 3.5,
    socialSecurityAge: 67,
    socialSecurityMonthly: 4500,

    housing: {
      type: 'own',
      monthlyPayment: 2600,
      startYear: 0,
      endYear: 18,  // 18 years to pay off by retirement at 50
      propertyValue: 520000,
      downPayment: 110000,
      mortgageRate: 6.5,
      mortgageTerm: 18,
      propertyTaxRate: 1.3,
      insuranceMonthly: 180,
      maintenanceRate: 1,
      appreciationRate: 3.5,
      rentGrowthRate: 0
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 4000,  // Comfortable but mindful spending
      healthcare: 600,
      transportation: 700,
      entertainment: 700,
      other: 500
    }
  },

  {
    name: 'Teacher Salary',
    currentAge: 28,
    retireAge: 60,  // Teachers can retire at 60 with full pension (30+ years)
    autoCalculateRetireAge: false,  // Fixed - pension kicks in at specific age
    currentSalary: 58000,  // Slightly higher - average US teacher
    salaryGrowthRate: 2.5,  // Steps/cola increases
    currentPortfolio: 25000,  // 403(b) and savings
    monthlyContribution: 650,  // 403(b) + required pension contribution
    expectedReturn: 7,
    inflation: 3,
    retirementIncome: 2900,  // Pension: ~60% of final salary after 30 years
    retirementExpenses: 3200,  // Lower - no commute, paid off home eventually
    safeWithdrawalRate: 4,  // Can be higher with guaranteed pension income
    socialSecurityAge: 67,
    socialSecurityMonthly: 1900,  // May be reduced due to pension (WEP)

    housing: {
      type: 'own',  // Teachers buy modest homes in areas they teach
      monthlyPayment: 1400,  // Affordable mortgage
      startYear: 0,
      endYear: 30,  // Pay off before retirement
      propertyValue: 280000,  // Modest home
      downPayment: 40000,  // Saved during first years
      mortgageRate: 6.5,
      mortgageTerm: 30,
      propertyTaxRate: 1.2,
      insuranceMonthly: 120,
      maintenanceRate: 1,
      appreciationRate: 3,
      rentGrowthRate: 0
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 2400,  // Modest - teachers live frugally
      healthcare: 200,  // Often good benefits through school district
      transportation: 350,  // Reliable used car
      entertainment: 250,  // Modest hobbies
      other: 200
    }
  },

  {
    name: 'Military Career (20 years)',
    currentAge: 22,
    retireAge: 42,  // 20 years of service = military retirement
    autoCalculateRetireAge: false,  // Fixed - must serve 20 years
    currentSalary: 52000,  // E-5 base + BAH + BAS (total comp)
    salaryGrowthRate: 4,  // Promotions and longevity increases
    currentPortfolio: 15000,  // TSP (Thrift Savings Plan) with matching
    monthlyContribution: 750,  // Max TSP matching (5%) + additional savings
    expectedReturn: 7,
    inflation: 3,
    retirementIncome: 3100,  // 50% of base pay after 20 years (~$6200/mo final pay)
    retirementExpenses: 3800,  // Post-military - will need to pay for housing
    safeWithdrawalRate: 4,  // Can be higher with guaranteed pension
    socialSecurityAge: 67,
    socialSecurityMonthly: 2200,  // Military + second career

    housing: {
      type: 'rent',  // Military housing allowance covers rent
      monthlyPayment: 0,  // BAH (Basic Allowance for Housing) covers this
      startYear: 0,
      endYear: 20,  // After retirement at 42, need to pay rent/buy home
      propertyValue: 0,
      downPayment: 0,
      mortgageRate: 0,
      mortgageTerm: 0,
      propertyTaxRate: 0,
      insuranceMonthly: 0,
      maintenanceRate: 0,
      appreciationRate: 0,
      rentGrowthRate: 0
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 2200,  // Low due to commissary, PX access
      healthcare: 0,  // TRICARE (free military healthcare)
      transportation: 350,  // One reliable car
      entertainment: 400,  // Young, active lifestyle
      other: 250
    }
  },

  {
    name: 'Young Schoolteacher (Age 25)',
    currentAge: 25,
    retireAge: 65,
    autoCalculateRetireAge: true,
    currentSalary: 48000,
    salaryGrowthRate: 2.5,
    currentPortfolio: 8000,
    monthlyContribution: 400,
    expectedReturn: 7,
    inflation: 3,
    retirementIncome: 0,
    retirementExpenses: 3200,
    safeWithdrawalRate: 3.5,
    socialSecurityAge: 67,
    socialSecurityMonthly: 1700,

    housing: {
      type: 'rent',
      monthlyPayment: 1100,
      startYear: 0,
      endYear: 40,
      propertyValue: 0,
      downPayment: 0,
      mortgageRate: 0,
      mortgageTerm: 0,
      propertyTaxRate: 0,
      insuranceMonthly: 0,
      maintenanceRate: 0,
      appreciationRate: 0,
      rentGrowthRate: 3
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 2700,
      healthcare: 200,
      transportation: 350,
      entertainment: 200,
      other: 250
    }
  },

  {
    name: 'New Grad with Student Debt',
    currentAge: 22,
    retireAge: 65,
    autoCalculateRetireAge: true,
    currentSalary: 62000,
    salaryGrowthRate: 4,
    currentPortfolio: 2000,
    monthlyContribution: 300,
    expectedReturn: 7.5,
    inflation: 3,
    retirementIncome: 0,
    retirementExpenses: 3800,
    safeWithdrawalRate: 3.5,
    socialSecurityAge: 67,
    socialSecurityMonthly: 2000,

    housing: {
      type: 'rent',
      monthlyPayment: 1300,
      startYear: 0,
      endYear: 43,
      propertyValue: 0,
      downPayment: 0,
      mortgageRate: 0,
      mortgageTerm: 0,
      propertyTaxRate: 0,
      insuranceMonthly: 0,
      maintenanceRate: 0,
      appreciationRate: 0,
      rentGrowthRate: 3
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 3200,
      healthcare: 250,
      transportation: 400,
      entertainment: 250,
      other: 450
    }
  },

  {
    name: 'Mid-Career Professional (Age 40)',
    currentAge: 40,
    retireAge: 60,
    autoCalculateRetireAge: true,
    currentSalary: 110000,
    salaryGrowthRate: 3,
    currentPortfolio: 250000,  // Increased from 180k - more realistic for age 40 professional
    monthlyContribution: 2500,  // Increased from 2200 - ~27% savings rate
    expectedReturn: 7.5,
    inflation: 3,
    retirementIncome: 0,
    retirementExpenses: 4500,  // Reduced from 5000 - more realistic post-retirement
    safeWithdrawalRate: 3.5,
    socialSecurityAge: 67,
    socialSecurityMonthly: 2800,

    housing: {
      type: 'own',
      monthlyPayment: 1900,  // Reduced from 2100 - more affordable
      startYear: 0,
      endYear: 20,
      propertyValue: 420000,  // Reduced from 480k
      downPayment: 84000,
      mortgageRate: 6.5,
      mortgageTerm: 20,
      propertyTaxRate: 1.2,
      insuranceMonthly: 140,
      maintenanceRate: 1,
      appreciationRate: 3.5,
      rentGrowthRate: 0
    },

    kids: [],

    lifestyle: {
      monthlyExpenses: 3500,  // Reduced from 4000 - living within means
      healthcare: 400,  // Reduced from 450
      transportation: 600,  // Reduced from 650
      entertainment: 450,  // Reduced from 500
      other: 400  // Reduced from 450
    }
  }
];

/**
 * Create sample visions with proper structure for the app
 */
export function createSampleVisions() {
  return sampleVisions.map((template, index) => ({
    id: Date.now() + index,
    createdAt: Date.now(),
    lastModified: Date.now(),

    // Copy all template properties
    ...template,

    // Add default properties that aren't in the template
    housingStatus: template.housing.type === 'own' ? 'already-own' : 'renting-forever',
    homePurchaseAge: template.housing.type === 'own' ? template.currentAge : 35,
    currentMortgagePayment: template.housing.monthlyPayment,
    yearsLeftOnMortgage: template.housing.type === 'own' ? template.housing.endYear : 0,
    hasPartner: template.currentSalary > 150000, // DINK couple
    partnerAge: template.currentAge + 1,
    partnerIncomeStart: 0,
    partnerIncomeStartsInYears: 0,
    partnerRetireAge: template.retireAge,
    kids: template.kids.length || 0,
    firstChildAge: 32,
    childSpacing: 3,
    incomeGrowth: template.salaryGrowthRate / 100,
    location: 'medium-cost',
    homePrice: template.housing.propertyValue || 400000,
    downPayment: template.housing.downPayment || 80000,
    mortgageRate: template.housing.mortgageRate || 7,

    // Current Assets
    currentCash: 0,
    current401k: template.currentPortfolio || 0,
    currentInvestments: 0,
    currentHomeEquity: 0,
    currentOtherAssets: 0,

    // NEW: Debts, events, career
    debts: [],
    oneTimeEvents: [],
    careerTimeline: [],
    incomeOverrides: {},

    // College savings
    collegeSavings: {
      enabled: false,
      monthlyContribution: 0,  // DEPRECATED: Auto-calculated based on targetCoveragePercent
      currentBalance: 0,
      costPerYear: 30000,
      targetCoveragePercent: 100  // What % of college costs to cover
    },

    financialSettings: undefined,
    lifestyleExpenses: [],
    lifestyleChoices: [],

    // Precision expenses - derive from template to avoid inflating costs
    precisionExpenses: (() => {
      // Start with housing costs
      const housingCosts = {
        monthlyRent: template.housing.type === 'rent' ? template.housing.monthlyPayment : 0,
        monthlyMortgage: template.housing.type === 'own' ? template.housing.monthlyPayment : 0,
        propertyTax: template.housing.type === 'own' ? (template.housing.propertyValue * (template.housing.propertyTaxRate / 100) / 12) : 0,
        homeInsurance: template.housing.insuranceMonthly || 0,
        utilities: template.housing.type === 'rent' && template.housing.monthlyPayment === 0 ? 0 : 250, // Free utilities if no rent (military)
        homeMaintenance: template.housing.type === 'own' ? 200 : 0,
      };

      // Calculate total housing
      const totalHousing = Object.values(housingCosts).reduce((sum, val) => sum + val, 0);

      // Known fixed costs from template
      const retirement401k = template.monthlyContribution || 500;
      const healthInsurance = template.lifestyle.healthcare || 0;
      const transportation = template.lifestyle.transportation || 400;
      const entertainment = template.lifestyle.entertainment || 200;
      const other = template.lifestyle.other || 200;

      // Remaining budget for other lifestyle expenses
      const targetMonthlyExpenses = template.lifestyle.monthlyExpenses || 2500;
      const allocatedExpenses = totalHousing + healthInsurance + transportation + entertainment + other;
      const remainingBudget = Math.max(0, targetMonthlyExpenses - allocatedExpenses);

      // Distribute remaining budget across typical categories (proportionally)
      // Typical breakdown: groceries 35%, dining 25%, shopping 20%, personal 10%, misc 10%
      const groceries = Math.round(remainingBudget * 0.35);
      const diningOut = Math.round(remainingBudget * 0.25);
      const shopping = Math.round(remainingBudget * 0.20);
      const personalCare = Math.round(remainingBudget * 0.10);
      const subscriptions = Math.round(remainingBudget * 0.10);

      return {
        ...housingCosts,
        carPayment: 0, // Keep separate from transportation budget
        carInsurance: Math.round(transportation * 0.4), // ~40% of transport budget
        gas: Math.round(transportation * 0.4), // ~40% of transport budget
        publicTransit: 0,
        carMaintenance: Math.round(transportation * 0.2), // ~20% of transport budget
        groceries,
        diningOut,
        shopping,
        personalCare,
        healthInsurance,
        medicalExpenses: healthInsurance > 0 ? Math.round(healthInsurance * 0.5) : 0, // 50% of insurance as out-of-pocket
        entertainment: Math.round(entertainment * 0.5), // Half for entertainment category
        hobbies: Math.round(entertainment * 0.3), // 30% for hobbies
        travel: Math.round(entertainment * 0.2), // 20% for travel
        subscriptions,
        gym: 50,
        retirement401k,
        otherSavings: 0, // Don't double-count savings
        debtPayments: 0,
        childcare: 0,
        childEducation: 0,
        childActivities: 0,
        childOther: 0,
        gifts: 50,
        charity: 50,
        petExpenses: 0,
        other
      };
    })()
  }));
}
